import React from 'react';
import { cva } from 'class-variance-authority';
// Imported through the `@` alias on purpose: it is the path shadcn's own components
// use, and this is the one usage that proves vite.config.mjs's `resolve.alias` and jest's
// `moduleNameMapper` both resolve it. A pasted-in shadcn component depends on that.
import cn from '@/shared/ui/primitives/cn';

/**
 * shadcn's Alert, keeping the `variant` names the two call sites already passed so they
 * did not have to change.
 *
 * The variants are Bootstrap's four (`success`, `danger`, `warning`, `info`) because that
 * is what the callers pass. Success, warning and danger are the same boxes as the growls
 * (`.alert-*` in main.css) and MUI's alerts: one colour pair per tone, on the notice's
 * inset and radius. They were white with a coloured border, which nothing else used.
 *
 * Note this is not the MUI `Alert`: sixteen screens use that one with `severity` and
 * `sx`, and those stay where they are, themed from the same tokens.
 */
export const alertVariants = cva(
  ['tw-block tw-rounded-track tw-border tw-border-solid tw-px-4 tw-py-3 tw-text-body tw-leading-5'],
  {
    variants: {
      variant: {
        info: 'tw-bg-accent-surface tw-border-accent-border tw-text-accent-dark',
        success: 'tw-bg-ok-bg tw-border-ok-bg tw-text-ok-ink',
        warning: 'tw-bg-warn-bg tw-border-warn-bg tw-text-warn-ink',
        danger: 'tw-bg-alert-danger-bg tw-border-alert-danger-border tw-text-alert-danger-ink',
      },
    },
    defaultVariants: { variant: 'info' },
  }
);

const Alert = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
));
Alert.displayName = 'Alert';

export default Alert;
